import type { ChatAgentConfig } from '@app/chat/models/chat.types';
import { FeatureFlag } from '@app/core/feature-flags/feature-flag.types';
import { ICache } from '@auth0/auth0-angular';

export type OcrKeysAndroidAndIos = {
  ios: OcrKeys;
  android: OcrKeys;
};

export type OcrKeys = {
  idApiKey: string;
};

export interface Employee {
  type: EmployeeType;
}

export enum EmployeeType {
  Candidate = 1,
  Freelancer = 2,
}

export type ClientConfig = {
  document: { ocr: OcrKeysAndroidAndIos };
  action: { separator: { document: string } };
  job: JobOptions;
  featureFlags: FeatureFlag[];
  employee: Employee;
  timeRegistration: {
    breaks: BreaksOptions;
    comment: CommentOptions;
    canContinueWithAnotherActivity: boolean;
  };
  work: WorkOptions;
  holidayRequest: HolidayRequestOptions;
  applicationLanguage: CultureInfo;
  eventCalendar: EventCalendarOptions;
  chatBot?: ChatBotOptions;
};

export interface BreaksOptions {
  maxBreaksCount: number;
  requireBreakAfterMinutes: number;
}

export interface CommentOptions {
  maxLength: number;
}

export interface ConfigData {
  keys: {
    googleMapsApiKey: string;
    googleRecaptchaSiteKey: string;
    google: {
      ios: GoogleApiKeys;
      android: GoogleApiKeys;
      web: GoogleApiKeys;
    };
  };
  links: {
    appStore: string;
    playStore: string;
  };
  paySlipTutorialUris: PayslipTutorialUris;
  cultureInfos: CultureInfo[];
  preferredCountries: CountryInfo[];
  preferredLanguages: LanguageInfo[];
  defaultCountry: CountryConfig;
  security?: {
    auth0?: Auth0Config;
  };
  languages?: LanguageInfo[];
  cultures?: CultureInfo[];
  featureFlags?: FeatureFlag[];
  spotCheck: {
    domain: string;
    targetToken: string;
  };
  job?: JobOptions;
  chatBot?: ChatBotOptions;
}

export interface Auth0Config {
  audience: string;
  clientId: string;
  domain: string;
  securedRoutes?: string[];
  allowedList: any[];
  redirectUri?: string;
  useRefreshTokens?: boolean;
  cache?: ICache;
  cacheLocation?: 'localstorage' | 'memory';
}

export interface GoogleApiKeys {
  mapsApiKey: string;
}

export interface MaintenanceConfig {
  maintenance: {
    showAnnouncementMinutesBefore: number;
    times: { start: string; end: string; announcement: string }[];
  };
}

export type PayslipTutorialUris = {
  eng: string;
  pol: string;
  ron: string;
  spa: string;
  slk: string;
  lit: string;
  hun: string;
};

export interface CultureInfo {
  cultureCode: string;
  languageAlpha2Code: string;
  languageAlpha3Code?: string;
  languageNativeName: string;
  countryAlpha2Code: string;
  languageId: string;
}

export interface CountryConfig {
  alpha2Code: string;
  alpha3Code: string;
  name: string;
  nationality: string;
  isDefaultCountryForPhoneNumber: boolean;
}

export interface CountryInfo {
  Id: string;
  alpha2Code: string;
  alpha3Code?: string;
  name: string;
  nationality: string;
}

export interface LanguageInfo {
  id: string;
  alpha2Code: string;
  alpha3Code?: string;
  name: string;
  nativeName: string;
}

export interface WorkOptions {
  workedHoursLastWeek: {
    isVisible: boolean;
  };
  workedHoursPastWeeks: {
    isVisible: boolean;
  };
  nextPaymentDate: {
    isVisible: boolean;
  };
}

export interface HolidayRequestOptions {
  startTime: {
    isVisible: boolean;
  };
  endTime: {
    isVisible: boolean;
  };
  entireDay: {
    isVisible: boolean;
  };
  useReservations: {
    isVisible: boolean;
  };
  remark: {
    isVisible: boolean;
  };
}

export interface JobOptions {
  details?: {
    numberOfVisibleAttributes: number;
  };
  savedJobs: {
    isVisible: boolean;
  };
  chatBotJobSearch?: {
    isVisible: boolean;
  };
}

export interface ChatBotOptions {
  jobSearch?: ChatAgentConfig;
  knowledge?: ChatAgentConfig;
}

export interface EventCalendarOptions {
  addUnavailability: {
    isVisible: boolean;
  };
  preferredWorkSchedule: {
    isVisible: boolean;
  };
  shiftsRadius: {
    isVisible: boolean;
  };
  includeCancelled: {
    isVisible: boolean;
  };
  calendarSubscription?: {
    isVisible: boolean;
  };
}

export const COVEBO_MINIMAL_VERSION_HEADER = 'x-hohr-client-min-version';
